import { makeAutoObservable, runInAction } from 'mobx';

import { generateStripeInvoicePreview as generateStripeInvoicePreviewAPI } from './api';
import {
  logError,
  tryGetFirstError,
} from '../../../shared/helpers/functions/api';
import {
  GenerateStripeInvoicePreviewMutation,
  StripeSubscriptionItemInput,
} from '../../../shared/graphql/graphql';
import type { IRootStore } from '../rootStore/RootStore';

export default class SubscriptionPreviewStore {
  rootStore: IRootStore;

  inProgress = false;

  preview:
    | GenerateStripeInvoicePreviewMutation['generateStripeInvoicePreview']
    | null = null;

  error: Error | null = null;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  reset() {
    this.inProgress = false;
    this.preview = null;
    this.error = null;
  }

  get subscriptionPreviewGenerated() {
    return !!this.preview && !this.inProgress;
  }

  get subscriptionPreviewError() {
    return !!this.error;
  }

  async generateSubscriptionPreview({
    customerId,
    country,
    items,
    promocode,
  }: {
    customerId: string;
    country: string; // alpha2 code
    items: StripeSubscriptionItemInput[];
    promocode?: string;
  }) {
    this.inProgress = true;
    this.error = null;

    let typedError: Error | null = null;

    try {
      const { data } = await generateStripeInvoicePreviewAPI({
        customerId,
        promocode: promocode ?? null,
        address: { country },
        items,
      });

      if (!data?.generateStripeInvoicePreview) {
        throw new Error(
          '[SubscriptionPreviewStore][generateSubscriptionPreview] Data is empty.',
        );
      }

      runInAction(() => {
        this.preview = data.generateStripeInvoicePreview;
      });
    } catch (error) {
      typedError = tryGetFirstError(error);
      runInAction(() => {
        this.error = typedError;
        this.preview = null;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.inProgress = false;
      });
    }

    return { error: typedError };
  }
}
